import { buildS3ObjectUrl, DEFAULT_S3_REGION } from "@repo/utils";
import { AxiosError } from "axios";
import { useCallback, useRef, useState } from "react";
import type { DropEvent, FileRejection } from "react-dropzone";
import { toast } from "react-toastify";
import { sendRequest } from "@/api/clients/axios";
import type { Doc } from "@/types/models/delivery.types";

const presignedUrlEndpoint: string = import.meta.env.VITE_GET_PRESIGNED_URL as string;
const s3Region = (import.meta.env.VITE_AWS_REGION as string | undefined) || DEFAULT_S3_REGION;

function getFileNameWithoutExtension(fileName: string) {
  return fileName.substring(0, fileName.lastIndexOf(".")) || fileName;
}

export type UploadedFileMetadata = Record<string, unknown>;

export type UploadedFile = {
  name: string;
  key: string;
  url: string;
  originalFile: File;
  metadata: UploadedFileMetadata;
  type: string;
};

type UseUploadFileProps = {
  maxFileSize: number;
  maxFiles: number;
  uploadedFiles: Doc[];
  metadata: {
    folder: string;
    subFolder: string;
  };
  forceAllowEdit?: boolean;
  errorMessages?: {
    maxSize?: string;
    maxFiles?: string;
    fileType?: string;
  };
  uploadPath?: string;
  id?: string | number;
  onFilesUploaded?: (newFiles: UploadedFile[]) => void;
  fileName?: string;
};

function useUploadFile({
  maxFileSize,
  maxFiles,
  uploadedFiles,
  metadata,
  errorMessages,
  fileName,
  onFilesUploaded,
}: UseUploadFileProps) {
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Keep refs for smooth progress animation without triggering excessive re-renders
  const progressRef = useRef(0);
  const animationFrame = useRef<number | null>(null);
  const lastReportedProgress = useRef(0);

  const smoothSetProgress = useCallback((targetProgress: number) => {
    if (animationFrame.current !== null) {
      cancelAnimationFrame(animationFrame.current);
    }

    const animate = () => {
      progressRef.current += (targetProgress - progressRef.current) * 0.1;
      if (Math.abs(progressRef.current - targetProgress) < 0.5) {
        progressRef.current = targetProgress;
      } else {
        animationFrame.current = requestAnimationFrame(animate);
      }

      // Only update state if progress changed by at least 1% to reduce re-renders
      const roundedProgress = Math.round(progressRef.current);
      if (Math.abs(roundedProgress - lastReportedProgress.current) >= 1) {
        lastReportedProgress.current = roundedProgress;
        setUploadProgress(roundedProgress);
      }
    };

    animationFrame.current = requestAnimationFrame(animate);
  }, []);

  const handleError = useCallback((message: string) => {
    setErrorMessage(message);
    toast.error(message, { position: "bottom-right" });
  }, []);

  const validateFileRejections = useCallback(
    (fileRejections: FileRejection[]) => {
      if (fileRejections.length === 0) {
        return true;
      }

      const rejection = fileRejections[0];
      const code = rejection.errors[0]?.code;

      if (code === "file-too-large") {
        handleError(
          errorMessages?.maxSize != null && errorMessages.maxSize.length > 0
            ? errorMessages.maxSize
            : `File is too large. Max size: ${maxFileSize}MB`
        );
      } else if (code === "file-invalid-type") {
        handleError(
          errorMessages?.fileType != null && errorMessages.fileType.length > 0
            ? errorMessages.fileType
            : "Invalid file type"
        );
      } else {
        handleError(`File rejected: ${rejection.errors[0]?.message}`);
      }
      return false;
    },
    [errorMessages, maxFileSize, handleError]
  );

  const validateFileCount = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        return false;
      }

      if (uploadedFiles.length + acceptedFiles.length > maxFiles) {
        handleError(
          errorMessages?.maxFiles != null && errorMessages.maxFiles.length > 0
            ? errorMessages.maxFiles
            : `You can upload a maximum of ${maxFiles} files.`
        );
        return false;
      }
      return true;
    },
    [uploadedFiles.length, maxFiles, errorMessages, handleError]
  );

  const uploadSingleFile = useCallback(
    async (file: File): Promise<UploadedFile> => {
      const nameWithoutExt = getFileNameWithoutExtension(file.name);
      const ext = file.name.split(".").pop()?.toLowerCase();

      // ✅  Fix folder path and make sure extension is included if missing
      let processedFolder = metadata.folder;
      if (processedFolder.endsWith("/")) {
        processedFolder = `${processedFolder}${nameWithoutExt}`;
      }

      // Add extension if not present in the processed folder
      if (ext != null && ext !== "" && !processedFolder.toLowerCase().endsWith(`.${ext}`)) {
        processedFolder = `${processedFolder}.${ext}`;
      }

      // ✅  Build upload payload
      const payload = {
        path: processedFolder,
        fileName: fileName != null && fileName !== "" ? fileName : nameWithoutExt,
        type: file.type,
        fileSize: file.size,
        folder: processedFolder,
        subFolder: metadata.subFolder,
        metadata: {
          ...metadata,
          originalFileName: nameWithoutExt,
          fileSize: file.size,
          fileType: file.type,
          processedFolder,
        },
      };

      // ✅  Get the presigned URL
      const { data } = await sendRequest<
        { path: string; originalFileName: string },
        {
          key: string;
          url: string;
          expiresIn: number;
          bucket: string;
        }
      >(
        "post",
        presignedUrlEndpoint,
        {
          path: payload.path,
          originalFileName: payload.metadata.originalFileName,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!(data?.key && data?.url && data?.bucket)) {
        throw new Error("Invalid response when requesting presigned URL.");
      }

      // ✅  Upload file using XHR (with progress tracking)
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100);
            smoothSetProgress(percent);
          }
        };
        xhr.open("PUT", data.url, true);
        xhr.setRequestHeader("Content-Type", file.type);

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response);
          else reject(new Error(`Upload failed with status ${xhr.status}`));
        };

        xhr.onerror = () => reject(new Error("Upload failed due to network error"));
        xhr.send(file);
      });
      // ✅ 6. Return uploaded file info — derive public URL from key (never strip the PUT URL)
      const uploaded: UploadedFile = {
        name: file.name,
        key: data.key,
        url: buildS3ObjectUrl({
          bucket: data.bucket,
          key: data.key,
          region: s3Region,
        }),
        originalFile: file,
        type: file.type,
        metadata: payload.metadata,
      };

      // console.log("✅ Final uploaded file:", uploaded);
      return uploaded;
    },
    [metadata, fileName, smoothSetProgress]
  );

  const resetUploadState = useCallback(() => {
    setLoading(false);
    setUploadProgress(0);
    progressRef.current = 0;
    lastReportedProgress.current = 0;
    if (animationFrame.current !== null) {
      cancelAnimationFrame(animationFrame.current);
      animationFrame.current = null;
    }
  }, []);

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[], _event: DropEvent) => {
      setErrorMessage(null);
      setUploadProgress(0);

      if (!validateFileRejections(fileRejections)) {
        return;
      }
      if (!validateFileCount(acceptedFiles)) {
        return;
      }

      setLoading(true);

      try {
        const uploadPromises = acceptedFiles.map(uploadSingleFile);
        const newUploadedFiles = await Promise.all(uploadPromises);

        // toast.success("Files uploaded successfully");

        if (onFilesUploaded) {
          onFilesUploaded(newUploadedFiles);
        }
      } catch (err: unknown) {
        let message = "Error uploading file(s). Please try again.";
        if (err instanceof AxiosError) {
          const data: unknown = err.response?.data;
          const dataMessage =
            data !== null && data !== undefined && typeof data === "object" && "message" in data
              ? (data as { message?: unknown }).message
              : undefined;
          const fromPayload =
            typeof dataMessage === "string" && dataMessage.length > 0 ? dataMessage : undefined;
          message = fromPayload ?? err.message ?? message;
        } else if (err instanceof Error && err.message.length > 0) {
          message = err.message;
        }
        handleError(message);
      } finally {
        resetUploadState();
      }
    },
    [
      validateFileRejections,
      validateFileCount,
      handleError,
      uploadSingleFile,
      onFilesUploaded,
      resetUploadState,
    ]
  );

  return { onDrop, loading, errorMessage, uploadProgress };
}

export default useUploadFile;
